import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

interface UseSocketOptions {
  url: string;
  jwtToken?: string;
  handlers?: Record<string, (data: any) => void>;
}

export function useSocket({ url, handlers, jwtToken }: UseSocketOptions) {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    if (!jwtToken) {
      return;
    }
    const socket = io(url, {
      transports: ['websocket'],
      query: {
        token: jwtToken,
      },
    });

    socketRef.current = socket;

    const handleConnect = () => {
      setConnected(true);
      setError(undefined);
      console.log('[socket] Connected');
      handlers?.['connect']?.(socket.id);
    };

    const handleDisconnect = (reason: string) => {
      setConnected(false);
      console.log('[socket] Disconnected:', reason);
      handlers?.['disconnect']?.(reason);
    };

    const handleConnectError = (err: any) => {
      setError(new Error(err?.data?.message));
      console.error('[socket] Connect error:', err);
      handlers?.['connect_error']?.(err);
    };

    const handleError = (error: any) => {
      setError(new Error(error?.data?.message));
      console.error('[socket] Error:', error);
      handlers?.['error']?.(error);
    };

    socket.on('connect', handleConnect);

    socket.on('disconnect', handleDisconnect);

    socket.on('connect_error', handleConnectError);

    socket.on('error', handleError);

    // Register custom event handlers
    Object.entries(handlers || {})
      .filter(
        (eventHandler) => !['connect', 'disconnect', 'connect_error'].includes(eventHandler[0]),
      )
      .forEach(([event, handler]) => {
        socket.on(event, handler);
      });

    console.log('[socket] Socket initialized', socket);

    return () => {
      console.log('[socket] Cleaning up socket connection', socket, socketRef.current);
      socketRef.current?.off('connect', handleConnect);
      socketRef.current?.off('disconnect', handleDisconnect);
      socketRef.current?.off('connect_error', handleConnectError);
      socketRef.current?.off('error', handleError);

      // Unregister custom event handlers
      Object.entries(handlers || {})
        .filter(
          (eventHandler) =>
            !['connect', 'disconnect', 'connect_error', 'error'].includes(eventHandler[0]),
        )
        .forEach(([event, handler]) => {
          socketRef.current?.off(event, handler);
        });
      console.log('[socket] Disconnecting socket', socket);
      socketRef.current?.disconnect();
    };
  }, [handlers, url, jwtToken]);

  const sendMessage = useCallback(
    (event: string, payload?: any, skipAck: boolean = true): Promise<any> => {
      return new Promise((resolve, reject) => {
        if (!socketRef.current?.connected) {
          reject(new Error('[socket] Not connected, cannot send'));
          return;
        }

        // Send the message
        if (payload) {
          socketRef.current.emit(event, payload);
        } else {
          socketRef.current.emit(event);
        }

        // If skipAck is true, resolve immediately without waiting for acknowledgment
        if (skipAck) {
          resolve(undefined);
          return;
        }

        let timeoutId: number;

        // Set up one-time listener for server_ack
        const handleAck = (response: any) => {
          clearTimeout(timeoutId);
          if (response?.code === 0) {
            resolve(response.data);
          } else {
            reject(new Error(response?.message || 'Server error'));
          }
        };

        // Listen for server_ack once
        socketRef.current.once('server_ack', handleAck);

        // Set up timeout (10 seconds)
        timeoutId = setTimeout(() => {
          socketRef.current?.off('server_ack', handleAck);
          reject(new Error('Timeout waiting for server acknowledgment'));
        }, 10000);
      });
    },
    [],
  );

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
  }, []);

  return {
    socket: socketRef.current,
    connected,
    error,
    sendMessage,
    disconnect,
  };
}
